import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiSession } from '@/lib/useApiSession';
import { addStatement, retractStatement } from '@/lib/useEntity';
import {
  ONTOLOGY_FIELDS,
  localName,
  useDescribedOntologies,
  type OntologyDescription,
} from '@/lib/ontologyAbout';
import { buttonVariants } from '@/components/ui/Button';
import { fieldClass } from '@/components/ui/field';
import { cn } from '@/lib/utils';

/** "About this ontology": one line under the control row, opened on demand, with the
 *  statements that describe the ontology, editable in Edit mode. */
export function OntologyAbout({
  graphs,
  writeGraph,
  canEdit,
}: {
  graphs: string[];
  writeGraph: string | null;
  canEdit: boolean;
}) {
  const about = useDescribedOntologies(graphs);
  const items = about.data ?? [];
  const [describing, setDescribing] = useState(false);
  const title = (d: OntologyDescription) => d.values.label?.[0]?.value ?? localName(d.iri);
  const summary =
    items.length === 0
      ? 'About this ontology: nothing describes it yet'
      : items.length === 1
        ? `About this ontology: ${title(items[0]!)}`
        : `About these ontologies: ${items.map(title).join(', ')}`;

  return (
    <details className="mb-4 rounded-md border border-border bg-card px-4 py-2">
      <summary className="cursor-pointer text-sm font-medium text-tres-text-primary">{summary}</summary>
      <div className="mt-2 max-h-96 space-y-4 overflow-y-auto">
        {about.error && (
          <p className="text-xs text-tres-error">The ontology's description could not be read: {(about.error as Error).message}</p>
        )}
        {items.map((d) => (
          <OntologyCard key={d.iri} d={d} writeGraph={writeGraph} canEdit={canEdit} />
        ))}
        {items.length === 0 && !about.isLoading && !describing && (
          <p className="text-xs text-tres-text-secondary">
            No ontology in these graphs carries a definition, description, comment, creator, contributor,
            license, rights or date.
            {canEdit ? ' Describe it here.' : ' Turn on Edit mode to describe it.'}
          </p>
        )}
        {items.length === 0 && canEdit && writeGraph && (
          describing ? (
            <DescribeForm writeGraph={writeGraph} onDone={() => setDescribing(false)} />
          ) : (
            <button
              type="button"
              onClick={() => setDescribing(true)}
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
            >
              Describe this ontology
            </button>
          )
        )}
      </div>
    </details>
  );
}

function OntologyCard({
  d,
  writeGraph,
  canEdit,
}: {
  d: OntologyDescription;
  writeGraph: string | null;
  canEdit: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="font-mono text-[11px] text-tres-text-secondary">{d.iri}</div>
      <dl className="grid grid-cols-[10rem_1fr] gap-x-3 gap-y-2 text-sm">
        {ONTOLOGY_FIELDS.map((f) => {
          const values = d.values[f.key] ?? [];
          if (!canEdit && values.length === 0) return null;
          return (
            <FieldRow
              key={f.key}
              iri={d.iri}
              field={f}
              values={values}
              graph={canEdit ? writeGraph : null}
            />
          );
        })}
      </dl>
    </div>
  );
}

function FieldRow({
  iri,
  field,
  values,
  graph,
}: {
  iri: string;
  field: (typeof ONTOLOGY_FIELDS)[number];
  values: { predicate: string; value: string }[];
  graph: string | null;
}) {
  const { token } = useApiSession();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(field.many ? '' : (values[0]?.value ?? ''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['ontology-about'] });

  const remove = async (v: { predicate: string; value: string }) => {
    if (!graph || !token) return null;
    const r = await retractStatement(
      { graph, subject: iri, predicate: v.predicate, object: { value: v.value } },
      token,
    );
    return r.ok || r.error === 'statement_gone' ? null : r.message;
  };
  const write = async () => {
    if (!graph || !token) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    let problem: string | null = null;
    if (!field.many) {
      for (const v of values) problem ??= await remove(v);
    }
    if (!problem && draft.trim()) {
      const r = await addStatement(
        { graph, subject: iri, predicate: field.predicate, object: { value: draft.trim() } },
        token,
      );
      if (!r.ok) problem = r.message;
    }
    setBusy(false);
    if (problem) setError(problem);
    else {
      setSaved(true);
      if (field.many) setDraft('');
      void refresh();
    }
  };
  const drop = async (v: { predicate: string; value: string }) => {
    setBusy(true);
    setError(null);
    const problem = await remove(v);
    setBusy(false);
    if (problem) setError(problem);
    else void refresh();
  };

  const editor = field.long ? (
    <textarea
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
        setSaved(false);
      }}
      rows={2}
      aria-label={field.name}
      className={cn(fieldClass('normal'), 'w-full text-sm')}
    />
  ) : (
    <input
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value);
        setSaved(false);
      }}
      aria-label={field.name}
      className={cn(fieldClass('normal'), 'w-full text-sm')}
    />
  );

  return (
    <>
      <dt className="pt-1 text-xs font-medium text-tres-text-secondary">{field.name}</dt>
      <dd className="min-w-0 space-y-1">
        {(field.many || !graph) &&
          values.map((v, i) => (
            <div key={`${i}-${v.value}`} className="flex items-start gap-2">
              <span className="whitespace-pre-wrap text-tres-text-primary">{v.value}</span>
              {graph && field.many && (
                <button
                  type="button"
                  onClick={() => void drop(v)}
                  disabled={busy}
                  aria-label={`Remove ${field.name.toLowerCase()} ${v.value}`}
                  className={buttonVariants({ variant: 'ghost', size: 'icon' })}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        {graph && (
          <div className="flex items-start gap-2">
            {editor}
            <button
              type="button"
              onClick={() => void write()}
              disabled={busy || (field.many ? !draft.trim() : draft.trim() === (values[0]?.value ?? ''))}
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
            >
              {busy ? 'Saving…' : field.many ? 'Add' : 'Save'}
            </button>
          </div>
        )}
        {saved && <p className="text-xs text-tres-status-active">Saved.</p>}
        {error && <p className="text-xs text-tres-error">{error}</p>}
      </dd>
    </>
  );
}

function DescribeForm({ writeGraph, onDone }: { writeGraph: string; onDone: () => void }) {
  const { token } = useApiSession();
  const queryClient = useQueryClient();
  const [titleText, setTitleText] = useState('');
  const [definition, setDefinition] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const iri = `:${titleText.replace(/[^A-Za-z0-9]+/g, ' ').trim().split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join('')}Ontology`;

  const create = async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    const writes = [
      { predicate: ':type', object: { uri: ':Ontology' } },
      { predicate: ':label', object: { value: titleText.trim() } },
      { predicate: ':definition', object: { value: definition.trim() } },
    ];
    for (const w of writes) {
      const r = await addStatement({ graph: writeGraph, subject: iri, ...w }, token);
      if (!r.ok) {
        setBusy(false);
        setError(r.message);
        return;
      }
    }
    setBusy(false);
    await queryClient.invalidateQueries({ queryKey: ['ontology-about'] });
    onDone();
  };

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-tres-text-secondary">
        Title
        <input value={titleText} onChange={(e) => setTitleText(e.target.value)} className={cn(fieldClass('normal'), 'mt-1 w-full text-sm')} />
      </label>
      <label className="block text-xs font-medium text-tres-text-secondary">
        Definition: what the ontology is for
        <textarea value={definition} onChange={(e) => setDefinition(e.target.value)} rows={3} className={cn(fieldClass('normal'), 'mt-1 w-full text-sm')} />
      </label>
      {titleText.trim() && <p className="text-xs text-tres-text-secondary">It will be written as {iri} in {writeGraph}.</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void create()}
          disabled={busy || !titleText.trim() || !definition.trim()}
          className={buttonVariants({ variant: 'primary', size: 'sm' })}
        >
          {busy ? 'Saving…' : 'Describe it'}
        </button>
        <button type="button" onClick={onDone} className={buttonVariants({ variant: 'ghost', size: 'sm' })}>
          Cancel
        </button>
      </div>
      {error && <p className="text-xs text-tres-error">{error}</p>}
    </div>
  );
}
